// EditSelfComplianceTemp.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch, BASE_URL } from "../api_call";
import { IoIosInformationCircle, IoIosClose } from "react-icons/io";
import { MdDelete, MdFileDownload } from "react-icons/md";
import "./Dashboard.css";

export default function EditSelfComplianceTemp() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [compliances, setCompliances] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  // ---------------- Evidence Modal State ----------------
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null); // { slfcmp_id, ... }
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [evidenceList, setEvidenceList] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceSaving, setEvidenceSaving] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");

  // ---------------- Fetch Compliance Instances ----------------
  useEffect(() => {
    const complianceId = localStorage.getItem("selected_compliance_id");
    if (!complianceId) return;

    const fetchComplianceInstances = async () => {
      try {
        const res = await apiFetch(`/compliance_temp/fetch/custom/${complianceId}`, { method: "GET" });
        const records = res?.data || [];

        const formatted = records.map((row) => ({
          slfcmp_id: row.slfcmp_id,
          compliance: row.slfcmp_title || row.slfcmp_particular || row.slfcmp_act,
          start_date: row.slfcmp_start_date,
          action_date: row.slfcmp_action_date,
          end_date: row.slfcmp_end_date,
          reminder: `${row.slfcmp_reminder_days ?? 0} Days`,
          status: row.slfcmp_status || "Pending",
          approver_email: row.slfcmp_approvers_email || "",
          long_description: row.slfcmp_long_description || "",
        }));

        setCompliances(formatted);
      } catch (err) {
        console.error("Failed to fetch self compliance instances:", err);
        setErrorMsg("❌ Failed to fetch compliances");
      }
    };

    fetchComplianceInstances();
  }, []);

  // ---------------- Approver Email Change ----------------
  const handleApproverChange = (index, value) => {
    setCompliances((prev) => {
      const updated = [...prev];
      updated[index].approver_email = value;
      return updated;
    });
  };

  // ---------------- Send to Approver ----------------
  // Attachments no longer travel with this request — they're uploaded ahead
  // of time via the evidence modal and the backend pulls them from
  // attachment_self for this compliance instance.
  const handleSendToApprover = async (item, index) => {
    if (!item.approver_email) {
      alert("Enter approver email");
      return;
    }

    const formData = new FormData();
    formData.append("approver_email", item.approver_email);
    formData.append("compliance_instance_id", item.slfcmp_id);

    try {
      const res = await fetch(`${BASE_URL}/compliance_temp/custom/send_to_approver`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await res.json();
      if (res.status !== 200) throw data;

      setSuccessMsg(
        data.emails_sent > 1
          ? `✅ Sent as ${data.emails_sent} emails (${data.attachments_sent} attachments)`
          : "✅ Email sent and compliance requested successfully"
      );
      setErrorMsg("");

      setCompliances((prev) =>
        prev.map((c) =>
          c.slfcmp_id === item.slfcmp_id ? { ...c, status: "Requested" } : c
        )
      );
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.error || "❌ Failed to send to approver");
      setSuccessMsg("");
    }
  };

  // ---------------- Edit ----------------
  const handleEdit = (slfcmpId) => {
    localStorage.setItem("slfcmpId", slfcmpId);
    navigate("/final-edit-self");
  };

  // ---------------- Delete All ----------------
  const handleDeleteAll = async () => {
    const complianceId = localStorage.getItem("selected_compliance_id");

    if (!complianceId) {
      alert("Custom compliance ID not found");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete all custom compliance records?"
    );
    if (!confirmDelete) return;

    try {
      const res = await apiFetch(
        `/compliance_temp/delete/custom/${complianceId}`,
        { method: "DELETE" }
      );

      setSuccessMsg(
        `✅ ${res?.instances_deleted || "All"} custom compliance records deleted successfully`
      );
      setErrorMsg("");

      setCompliances([]);
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.error || "❌ Failed to delete custom compliance records");
      setSuccessMsg("");
    }
  };

  // ---------------- Evidence Modal Handlers ----------------
  const openEvidenceModal = async (item, index) => {
    setActiveItem({ ...item, _index: index });
    setEvidenceFile(null);
    setEvidenceDescription("");
    setEvidenceError("");
    setEvidenceModalOpen(true);
    await fetchEvidenceList(item.slfcmp_id);
  };

  const closeEvidenceModal = () => {
    setEvidenceModalOpen(false);
    setActiveItem(null);
    setEvidenceFile(null);
    setEvidenceDescription("");
    setEvidenceList([]);
    setEvidenceError("");
  };

  const fetchEvidenceList = async (slfcmpId) => {
    setEvidenceLoading(true);
    try {
      const res = await apiFetch(`/compliance_temp/custom/evidence/${slfcmpId}`, {
        method: "GET",
      });
      setEvidenceList(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch evidence documents:", err);
      setEvidenceList([]);
    } finally {
      setEvidenceLoading(false);
    }
  };

  const handleSaveEvidence = async () => {
    if (!activeItem) return;

    if (!evidenceFile) {
      setEvidenceError("Please select a file to upload");
      return;
    }

    // Description is now a required field — a document can't be saved
    // without one, so validate before ever hitting the API.
    const trimmedDescription = evidenceDescription.trim();
    if (!trimmedDescription) {
      setEvidenceError("Please add a description for this document");
      return;
    }

    const formData = new FormData();
    formData.append("compliance_instance_id", activeItem.slfcmp_id);
    formData.append("description", trimmedDescription);
    // The raw File object already carries its original name and MIME type —
    // the backend reads both off it (file.filename / file.content_type) and
    // stores them in slfatc_name / slfatc_type, separate from slfatc_desc.
    formData.append("attachment", evidenceFile);

    setEvidenceSaving(true);
    setEvidenceError("");

    try {
      const res = await fetch(`${BASE_URL}/compliance_temp/custom/evidence/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await res.json();
      if (res.status !== 200) throw data;

      setEvidenceFile(null);
      setEvidenceDescription("");

      await fetchEvidenceList(activeItem.slfcmp_id);
    } catch (err) {
      console.error(err);
      setEvidenceError(err?.error || "❌ Failed to upload document");
    } finally {
      setEvidenceSaving(false);
    }
  };

  const handleDeleteEvidence = async (docId) => {
    const confirmDelete = window.confirm("Delete this document?");
    if (!confirmDelete) return;

    try {
      await apiFetch(`/compliance_temp/custom/evidence/delete/${docId}`, {
        method: "DELETE",
      });

      setEvidenceList((prev) => prev.filter((doc) => doc.id !== docId));
    } catch (err) {
      console.error(err);
      setEvidenceError(err?.error || "❌ Failed to delete document");
    }
  };

  // The download route is auth-protected, so a plain <a href> won't carry
  // the token — fetch the blob ourselves and trigger the save using the
  // original filename (slfatc_name) so it comes back exactly as uploaded.
  const handleDownloadEvidence = async (doc) => {
    try {
      const res = await fetch(`${BASE_URL}${doc.download_url}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `evidence_${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setEvidenceError("❌ Failed to download document");
    }
  };

  // ---------------- Auto-hide Success ----------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  const statusCounts = useMemo(() => {
    const counts = { total: 0, pending: 0, requested: 0, approved: 0 };

    compliances.forEach((item) => {
      counts.total++;
      const status = item.status?.toLowerCase();
      if (status === "pending") counts.pending++;
      else if (status === "requested") counts.requested++;
      else if (status === "approved") counts.approved++;
    });

    return counts;
  }, [compliances]);

  return (
    <div className="edit-compliance">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <header>
            <h2>Custom Instances</h2>
          </header>

          <div className="messages-top">
            {errorMsg && <div className="login-error">{errorMsg}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ background: "#e2e3e5", color: "#383d41", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Total: {statusCounts.total}
              </div>
              <div style={{ background: "#f8d7da", color: "#721c24", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Pending: {statusCounts.pending}
              </div>
              <div style={{ background: "#fff3cd", color: "#856404", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Requested: {statusCounts.requested}
              </div>
              <div style={{ background: "#d4edda", color: "#155724", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Approved: {statusCounts.approved}
              </div>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table" style={{ tableLayout: "fixed", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "180px" }}>COMPLIANCE</th>
                  <th style={{ width: "400px" }}>DESCRIPTION</th>
                  <th style={{ width: "100px" }}>START DATE</th>
                  <th style={{ width: "100px" }}>ACTION DATE</th>
                  <th style={{ width: "100px" }}>END DATE</th>
                  <th style={{ width: "90px" }}>REM. BEFORE</th>
                  <th style={{ width: "100px" }}>STATUS</th>
                  <th style={{ width: "120px" }}>APPROVER’S EMAIL</th>
                  <th style={{ width: "140px" }}>ATTACHMENTS</th>
                  <th style={{ width: "120px" }}>REQUEST APPROVAL</th>
                  <th style={{ width: "60px" }}>EDIT</th>
                </tr>
              </thead>
              <tbody>
                {compliances.length > 0 ? (
                  compliances.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>{item.compliance}</span>
                          {item.long_description && (
                            <IoIosInformationCircle
                              title={item.long_description}
                              style={{ cursor: "pointer", fontSize: "16px", color: "#1A1744" }}
                            />
                          )}
                        </div>
                      </td>

                      <td style={{ width: "400px", maxWidth: "400px" }}>
                        <div className="description-scroll">{item.long_description}</div>
                      </td>

                      <td style={{ width: "100px", wordBreak: "break-word" }}>{item.start_date}</td>
                      <td style={{ width: "100px", wordBreak: "break-word" }}>{item.action_date}</td>
                      <td style={{ width: "100px", wordBreak: "break-word" }}>{item.end_date}</td>
                      <td style={{ width: "90px", wordBreak: "break-word" }}>{item.reminder}</td>
                      <td
                        style={{
                          width: "100px",
                          color:
                            item.status.toLowerCase() === "approved"
                              ? "green"
                              : item.status.toLowerCase() === "requested"
                              ? "orange"
                              : "red",
                          fontWeight: 500,
                        }}
                      >
                        {item.status}
                      </td>

                      <td style={{ width: "120px" }}>
                        <input
                          type="email"
                          className="approver-input"
                          value={item.approver_email}
                          onChange={(e) => handleApproverChange(index, e.target.value)}
                          placeholder="Enter email"
                        />
                      </td>

                      {/* ---------------- Attach Evidence -> opens modal ---------------- */}
                      <td style={{ width: "140px", maxWidth: "140px", overflow: "hidden", textAlign: "center" }}>
                        <button
                          type="button"
                          className="custom-file-btn"
                          onClick={() => openEvidenceModal(item, index)}
                          style={{
                            width: "auto",
                            maxWidth: "120px",
                            boxSizing: "border-box",
                            padding: "6px 14px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            marginLeft: "4px",
                          }}
                        >
                          Attach Evidences
                        </button>
                      </td>

                      <td style={{ width: "120px" }}>
                        <button
                          className="action-btn primary"
                          onClick={() => handleSendToApprover(item, index)}
                          title="Send to approver"
                        >
                          ✔
                        </button>
                      </td>

                      <td style={{ width: "60px" }}>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(item.slfcmp_id)}
                          disabled={item.status.toLowerCase() === "approved" || item.status.toLowerCase() === "requested"}
                          style={{
                            cursor:
                              item.status.toLowerCase() === "approved" || item.status.toLowerCase() === "requested"
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              item.status.toLowerCase() === "approved" || item.status.toLowerCase() === "requested" ? 0.5 : 1,
                          }}
                        >
                          ✎
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "center", marginTop: "25px" }}>
            <button className="submit-btn" style={{ width: "95%" }} onClick={handleDeleteAll}>
              Delete All
            </button>
          </div>
        </main>
      </div>

      {/* ---------------- Evidence Modal ---------------- */}
      {evidenceModalOpen && activeItem && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeEvidenceModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              width: "640px",
              maxWidth: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              padding: "24px",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IoIosClose
              onClick={closeEvidenceModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                fontSize: "28px",
                cursor: "pointer",
                color: "#555",
              }}
            />

            <h3 style={{ marginTop: 0, marginBottom: "4px" }}>Attach Evidence</h3>
            <p style={{ margin: "0 0 16px", color: "#555", fontSize: "14px" }}>
              Compliance ID: <strong>{activeItem.slfcmp_id}</strong>
              {activeItem.compliance ? ` — ${activeItem.compliance}` : ""}
            </p>

            {evidenceError && (
              <div className="login-error" style={{ marginBottom: "12px" }}>
                {evidenceError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
              <label className="file-upload-label">
                <input
                  type="file"
                  hidden
                  onChange={(e) => setEvidenceFile(e.target.files[0] || null)}
                />
                <span className="custom-file-btn">
                  {evidenceFile ? evidenceFile.name : "Choose File"}
                </span>
              </label>

              <label style={{ fontSize: "13px", fontWeight: 600, color: "#333" }}>
                Description <span style={{ color: "#c0392b" }}>*</span>
              </label>
              <textarea
                placeholder="Add a description for this document (e.g. signed invoice copy)"
                value={evidenceDescription}
                onChange={(e) => setEvidenceDescription(e.target.value)}
                required
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: evidenceDescription.trim() ? "1px solid #ccc" : "1px solid #c0392b",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />

              <button
                type="button"
                className="submit-btn"
                onClick={handleSaveEvidence}
                disabled={evidenceSaving || !evidenceFile || !evidenceDescription.trim()}
                style={{
                  alignSelf: "flex-start",
                  padding: "8px 20px",
                  opacity: evidenceSaving || !evidenceFile || !evidenceDescription.trim() ? 0.6 : 1,
                  cursor: evidenceSaving || !evidenceFile || !evidenceDescription.trim() ? "not-allowed" : "pointer",
                }}
              >
                {evidenceSaving ? "Saving..." : "Save"}
              </button>
            </div>

            <h4 style={{ marginBottom: "8px" }}>Previously Uploaded Documents</h4>

            {evidenceLoading ? (
              <p style={{ fontSize: "14px", color: "#777" }}>Loading...</p>
            ) : evidenceList.length > 0 ? (
              <table className="data-table" style={{ width: "100%", fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>File Name</th>
                    <th style={{ textAlign: "left" }}>Description</th>
                    <th style={{ width: "60px" }}></th>
                    <th style={{ width: "60px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {evidenceList.map((doc) => (
                    <tr key={doc.id}>
                      <td style={{ wordBreak: "break-all" }}>
                        {doc.filename || `evidence_${doc.id}`}
                      </td>
                      <td style={{ color: doc.description ? "inherit" : "#999" }}>
                        {doc.description || "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <MdFileDownload
                          onClick={() => handleDownloadEvidence(doc)}
                          style={{ cursor: "pointer", color: "#1A1744", fontSize: "18px" }}
                          title="Download document"
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <MdDelete
                          onClick={() => handleDeleteEvidence(doc.id)}
                          style={{ cursor: "pointer", color: "#c0392b", fontSize: "18px" }}
                          title="Delete document"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ fontSize: "14px", color: "#777" }}>No documents uploaded yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}